import {
  DIRECT_LINE_OVERRIDE_KEY,
  DIRECT_LINE_REPLAY_WATERMARK,
} from '../shared/directLine';

/**
 * Normalizes the result of `postActivity` (and similar Direct Line calls) to a
 * Promise. The Microsoft Web Chat contract is an Observable that emits the
 * activity id on `next`, but some Direct Line transports (notably local
 * emulators) return a thenable instead. We accept either rather than throwing
 * `observable.subscribe is not a function` deep inside a `try/catch`.
 *
 * @param {unknown} source
 * @returns {Promise<unknown>}
 */
export const observableToPromise = source => {
  if (source && typeof source.then === 'function') {
    return Promise.resolve(source);
  }

  if (!source || typeof source.subscribe !== 'function') {
    return Promise.reject(
      new TypeError(
        'observableToPromise expected an Observable or Promise from postActivity',
      ),
    );
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let subscription;

    const unsubscribe = () => {
      if (typeof subscription?.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };

    subscription = source.subscribe({
      next: value => {
        if (settled) {
          return;
        }

        settled = true;
        resolve(value);
        unsubscribe();
      },
      error: error => {
        if (settled) {
          return;
        }

        settled = true;
        reject(error);
        unsubscribe();
      },
      complete: () => {
        if (settled) {
          return;
        }

        settled = true;
        resolve(null);
        unsubscribe();
      },
    });

    // If the observable settled synchronously inside `subscribe` (e.g. calls
    // observer.next/error/complete before returning), the inline
    // `unsubscribe()` calls above ran while `subscription` was still
    // undefined and were no-ops. Now that `subscription` is assigned, do the
    // unsubscribe to release any underlying resources.
    if (settled) {
      unsubscribe();
    }
  });
};

export const getActivityDedupKey = activity => {
  if (activity?.channelData?.messageId) {
    return activity.channelData.messageId;
  }

  if (activity?.id) {
    return activity.id;
  }

  const role = activity?.from?.role || '';
  const type = activity?.type || '';
  const text = typeof activity?.text === 'string' ? activity.text.trim() : '';
  const suggestedActions = activity?.suggestedActions?.actions
    ?.map(action => action?.title || '')
    .join('|');
  const metadataKey = [
    activity?.conversation?.id,
    activity?.replyToId,
    activity?.timestamp,
    activity?.localTimestamp,
  ]
    .filter(Boolean)
    .join('::');

  return `${type}::${role}::${text}::${suggestedActions || ''}::${metadataKey}`;
};

export const createDirectLineOptions = ({
  conversationId,
  domain,
  isLocalDirectLine,
  token,
}) => {
  const options = {
    domain,
    token,
  };

  if (isLocalDirectLine && conversationId) {
    options.conversationId = conversationId;
    options.watermark = DIRECT_LINE_REPLAY_WATERMARK;
  }

  return options;
};

export const getCreateDirectLineFactory = ({
  createDirectLine,
  directLineOverrideKey = DIRECT_LINE_OVERRIDE_KEY,
  win = window,
}) => {
  if (win.Cypress && typeof win[directLineOverrideKey] === 'function') {
    return win[directLineOverrideKey];
  }

  return createDirectLine;
};
